package org.catharsis.spendtel.feature.presentation

import io.github.aakira.napier.Napier
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.first
import org.catharsis.spendtel.core.api.AnalyticsApi
import org.catharsis.spendtel.core.api.AuthenticationRepo
import org.catharsis.spendtel.core.api.ExpenseReportRepo
import org.catharsis.spendtel.core.api.PaymentRequiredException
import org.catharsis.spendtel.core.api.PreferencesManager
import org.catharsis.spendtel.core.api.SpendTelService
import org.catharsis.spendtel.core.api.StatementStorageRepo
import org.catharsis.spendtel.core.arch.MviViewModel
import org.catharsis.spendtel.core.arch.ViewEffect
import org.catharsis.spendtel.core.arch.ViewEvent
import org.catharsis.spendtel.core.arch.ViewState
import org.catharsis.spendtel.core.platform.PendingReportStore
import org.catharsis.spendtel.core.platform.PurchaseReturn
import org.catharsis.spendtel.core.platform.StorePurchaseOutcome
import org.catharsis.spendtel.core.platform.checkoutReturnPaths
import org.catharsis.spendtel.core.platform.openCheckoutUrl
import org.catharsis.spendtel.core.platform.purchaseCancelledMessage
import org.catharsis.spendtel.core.platform.purchaseConsumableReport
import org.catharsis.spendtel.core.platform.usesNativeStoreBilling
import org.catharsis.spendtel.core.platform.pickStatementDocuments
import org.catharsis.spendtel.feature.data.BillingEntitlement
import org.catharsis.spendtel.feature.data.buyReportMessage
import org.catharsis.spendtel.feature.data.friendlyPurchaseError
import org.catharsis.spendtel.feature.data.normalizePaywallMessage
import org.catharsis.spendtel.feature.data.normalizePriceLabel
import org.catharsis.spendtel.feature.data.ExpenseReport
import org.catharsis.spendtel.feature.data.ExpenseReportResponse
import org.catharsis.spendtel.feature.data.EventName
import org.catharsis.spendtel.feature.data.StoragePathInput
import org.catharsis.spendtel.feature.data.SpendtelUser
import org.catharsis.spendtel.feature.data.toExpenseReport
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

@OptIn(ExperimentalTime::class)
class HomeViewModel(
    private val authenticationRepo: AuthenticationRepo,
    private val expenseReportRepo: ExpenseReportRepo,
    private val preferencesManager: PreferencesManager,
    private val statementStorageRepo: StatementStorageRepo,
    private val spendTelService: SpendTelService,
    private val analytics: AnalyticsApi,
) : MviViewModel<HomeEvent, HomeState, HomeEffect>(
    initialState = HomeState(),
) {
    init {
        launch { loadHomeState() }
    }

    override fun receiveEvent(event: HomeEvent) {
        when (event) {
            HomeEvent.OnSelectStatements -> selectUploadAndCreateReport()
            HomeEvent.OnSignOut -> signOut()
            HomeEvent.OnRefresh -> launch { loadHomeState() }
            HomeEvent.OnRefreshEntitlement -> launch { refreshEntitlement() }
            is HomeEvent.OnDeleteReport -> deleteReport(event.reportId)
            HomeEvent.OnDismissPaywall -> dismissPaywall()
            HomeEvent.OnPurchaseReport -> purchaseReport()
            HomeEvent.OnBuyReportCredit -> showBuyReportPaywall()
            is HomeEvent.OnPurchaseReturn -> handlePurchaseReturn(event.returnType)
        }
    }

    private suspend fun loadHomeState() {
        val keepUploadUi = latestState.isConfirmingPurchase ||
            latestState.isCreatingReport ||
            latestState.isUploadingStatements

        if (!keepUploadUi) {
            setState { copy(isLoading = true) }
        }

        val user = authenticationRepo.getCurrentUser()
        val isFirstVisit = preferencesManager.isFirstTimeOnHomeScreen.first()
        val reportsFromFirestore = user?.uid?.let { expenseReportRepo.getExpenseReports(it) } ?: emptyList()
        val mergedReports = mergeReports(reportsFromFirestore, latestState.expenseReports)
        val hasReports = mergedReports.isNotEmpty() || latestState.hasExpenseReports
        val entitlement = spendTelService.getEntitlement().getOrNull()

        if (isFirstVisit) {
            preferencesManager.setFirstTimeOnHomeScreen(isFirstTime = false)
        }

        setState {
            copy(
                isLoading = false,
                currentUser = user,
                isFirstVisit = isFirstVisit,
                hasExpenseReports = hasReports,
                showEmptyState = !hasReports,
                expenseReports = mergedReports,
                entitlement = entitlement ?: this.entitlement,
            )
        }
    }

    private suspend fun refreshEntitlement() {
        spendTelService.getEntitlement()
            .onSuccess { entitlement -> setState { copy(entitlement = entitlement) } }
    }

    private fun mergeReports(
        fromFirestore: List<ExpenseReport>,
        optimisticReports: List<ExpenseReport>,
    ): List<ExpenseReport> {
        if (optimisticReports.isEmpty()) return fromFirestore

        val firestoreIds = fromFirestore.map { it.id }.toSet()
        val pendingOptimistic = optimisticReports.filter { it.id !in firestoreIds }
        return (pendingOptimistic + fromFirestore).sortedByDescending { it.createdAtMillis }
    }

    private fun selectUploadAndCreateReport() {
        launch {
            if (latestState.isHomeBusy()) {
                val message = when {
                    latestState.isConfirmingPurchase || latestState.isPurchasing ->
                        "Please wait — we're finishing your purchase and creating your report."
                    latestState.isCreatingReport ->
                        "Your spending story is already being created."
                    else -> "Please wait for the current upload to finish."
                }
                emitEffect { HomeEffect.OnError(message) }
                return@launch
            }

            setState { copy(isPickingStatements = true, uploadError = null) }

            val pickResult = pickStatementDocuments()
            setState { copy(isPickingStatements = false) }

            pickResult
                .onFailure { error ->
                    val message = error.message?.takeUnless { it.contains("cancel", ignoreCase = true) }
                    if (message != null) {
                        setState { copy(uploadError = message) }
                        emitEffect { HomeEffect.OnError(message) }
                    }
                    return@launch
                }

            val pickedFiles = pickResult.getOrThrow()
            if (pickedFiles.isEmpty()) return@launch

            analytics.logClickEvent(
                EventName.UPLOAD_STATEMENTS_STARTED,
                mapOf("statement_count" to pickedFiles.size),
            )

            val userId = latestState.currentUser?.uid ?: authenticationRepo.getCurrentUser()?.uid
            if (userId.isNullOrBlank()) {
                val message = "You must be signed in to upload statements."
                setState { copy(uploadError = message) }
                emitEffect { HomeEffect.OnError(message) }
                return@launch
            }

            val entitlement = spendTelService.getEntitlement().getOrNull()
            if (entitlement != null) {
                setState { copy(entitlement = entitlement) }
                if (!entitlement.canCreateReport) {
                    showPaywall(
                        message = paywallMessage(entitlement),
                        priceLabel = entitlement.priceLabel,
                        pendingStoragePaths = null,
                    )
                    return@launch
                }
            }

            setState { copy(isUploadingStatements = true, uploadProgressLabel = "Uploading statements…") }

            val storagePaths = statementStorageRepo.uploadStatements(userId, pickedFiles)
                .getOrElse { error ->
                    val message = error.message ?: "Could not upload your statements. Please try again."
                    Napier.e("Upload failed: $error")
                    setState {
                        copy(
                            isUploadingStatements = false,
                            isCreatingReport = false,
                            uploadProgressLabel = null,
                            uploadError = message,
                        )
                    }
                    emitEffect { HomeEffect.OnError(message) }
                    return@launch
                }

            Napier.d("Uploaded ${storagePaths.size} statement(s)")
            analytics.logClickEvent(
                EventName.UPLOAD_STATEMENTS_COMPLETED,
                mapOf("statement_count" to storagePaths.size),
            )

            if (!storagePaths.all { it.storagePath.startsWith("users/$userId/") }) {
                val message = "Uploaded file paths do not match your account."
                setState {
                    copy(
                        isUploadingStatements = false,
                        isCreatingReport = false,
                        uploadProgressLabel = null,
                        uploadError = message,
                    )
                }
                emitEffect { HomeEffect.OnError(message) }
                return@launch
            }

            setState { copy(isUploadingStatements = false) }
            createReportFromStoragePaths(storagePaths)
        }
    }

    private suspend fun createReportFromStoragePaths(storagePaths: List<StoragePathInput>) {
        analytics.logClickEvent(
            EventName.REPORT_CREATE_STARTED,
            mapOf("statement_count" to storagePaths.size),
        )
        setState {
            copy(
                isConfirmingPurchase = false,
                isPurchasing = false,
                isCreatingReport = true,
                uploadProgressLabel = "Creating your spending story… this can take about 20 seconds.",
                uploadError = null,
                pendingStoragePaths = null,
            )
        }

        spendTelService.createExpenseReport(storagePaths)
            .onSuccess { response -> handleReportCreated(response) }
            .onFailure { error ->
                when (error) {
                    is PaymentRequiredException -> {
                        showPaywall(
                            message = error.paymentError.message,
                            priceLabel = error.paymentError.priceLabel,
                            pendingStoragePaths = storagePaths,
                        )
                    }
                    else -> {
                        val message = error.message ?: "Could not start expense report analysis."
                        Napier.e("createExpenseReport failed: $error")
                        analytics.logClickEvent(
                            EventName.REPORT_CREATE_FAILED,
                            mapOf("reason" to message),
                        )
                        setState {
                            copy(
                                isCreatingReport = false,
                                isConfirmingPurchase = false,
                                uploadProgressLabel = null,
                                uploadError = message,
                            )
                        }
                        PendingReportStore.save(storagePaths)
                        emitEffect { HomeEffect.OnError(message) }
                    }
                }
            }
    }

    private suspend fun handleReportCreated(response: ExpenseReportResponse) {
        Napier.d("Expense report created: ${response.reportId}")
        analytics.logClickEvent(
            EventName.REPORT_CREATE_COMPLETED,
            mapOf("report_id" to response.reportId),
        )
        val optimisticReport = response.toExpenseReport(
            createdAtMillis = Clock.System.now().toEpochMilliseconds(),
        )
        val updatedReports = (listOf(optimisticReport) + latestState.expenseReports)
            .distinctBy { it.id }
            .sortedByDescending { it.createdAtMillis }

        setState {
            copy(
                isCreatingReport = false,
                isConfirmingPurchase = false,
                isPurchasing = false,
                uploadProgressLabel = null,
                uploadError = null,
                hasExpenseReports = true,
                showEmptyState = false,
                expenseReports = updatedReports,
                pendingStoragePaths = null,
                paywall = null,
            )
        }
        emitEffect { HomeEffect.OnExpenseReportCreated(response) }
        emitEffect { HomeEffect.NavigateToExpenseReport(response.reportId) }
        loadHomeState()
    }

    private fun showPaywall(
        message: String,
        priceLabel: String,
        pendingStoragePaths: List<StoragePathInput>?,
    ) {
        analytics.logClickEvent(
            EventName.PAYWALL_SHOWN,
            mapOf(
                "has_pending_upload" to (pendingStoragePaths != null),
                "price_label" to normalizePriceLabel(priceLabel),
            ),
        )
        val normalizedPrice = normalizePriceLabel(priceLabel)
        setState {
            copy(
                paywall = PaywallUiState(
                    message = normalizePaywallMessage(message, normalizedPrice),
                    priceLabel = normalizedPrice,
                ),
                pendingStoragePaths = pendingStoragePaths,
                isCreatingReport = false,
                isUploadingStatements = false,
                uploadProgressLabel = null,
            )
        }
    }

    private fun dismissPaywall() {
        setState {
            copy(
                paywall = null,
                pendingStoragePaths = null,
                isPurchasing = false,
            )
        }
    }

    private fun showBuyReportPaywall() {
        launch {
            val entitlement = spendTelService.getEntitlement().getOrNull() ?: latestState.entitlement
            if (entitlement == null) {
                emitEffect { HomeEffect.OnError("Could not load billing status. Please try again.") }
                return@launch
            }
            setState { copy(entitlement = entitlement) }
            showPaywall(
                message = entitlement.buyReportMessage(),
                priceLabel = entitlement.priceLabel,
                pendingStoragePaths = null,
            )
        }
    }

    private fun purchaseReport() {
        launch {
            analytics.logClickEvent(EventName.PURCHASE_STARTED)
            setState {
                copy(
                    isPurchasing = true,
                    paywall = paywall?.copy(isPurchasing = true, errorMessage = null),
                )
            }

            latestState.pendingStoragePaths?.let { paths ->
                PendingReportStore.save(paths)
            }

            if (usesNativeStoreBilling()) {
                when (val outcome = purchaseConsumableReport()) {
                    StorePurchaseOutcome.Success ->
                        handlePurchaseReturn(PurchaseReturn.Success)
                    StorePurchaseOutcome.Cancelled ->
                        handlePurchaseReturn(PurchaseReturn.Cancelled)
                    is StorePurchaseOutcome.Failed -> {
                        Napier.e("RevenueCat purchase failed: ${outcome.message}")
                        setState {
                            copy(
                                isPurchasing = false,
                                paywall = paywall?.copy(
                                    isPurchasing = false,
                                    errorMessage = friendlyPurchaseError(outcome.message),
                                ),
                            )
                        }
                    }
                }
                return@launch
            }

            spendTelService.createCheckoutSession(checkoutReturnPaths()).onSuccess { response ->
                openCheckoutUrl(response.checkoutUrl)
            }.onFailure { error ->
                Napier.e("createCheckoutSession failed: $error")
                val message = error.message ?: "Could not start checkout."
                setState {
                    copy(
                        isPurchasing = false,
                        paywall = paywall?.copy(
                            isPurchasing = false,
                            errorMessage = friendlyPurchaseError(message),
                        ),
                    )
                }
            }
        }
    }

    private fun handlePurchaseReturn(returnType: PurchaseReturn) {
        launch {
            when (returnType) {
                PurchaseReturn.Success -> {
                    val pendingPaths = PendingReportStore.load() ?: latestState.pendingStoragePaths
                    val hasPendingReport = !pendingPaths.isNullOrEmpty()
                    val statementCount = pendingPaths?.size ?: 0

                    setState {
                        copy(
                            isPurchasing = false,
                            isConfirmingPurchase = true,
                            paywall = null,
                            uploadError = null,
                            pendingStoragePaths = pendingPaths,
                            uploadProgressLabel = if (hasPendingReport) {
                                pendingReportResumeLabel(statementCount)
                            } else {
                                "Confirming your payment…"
                            },
                        )
                    }

                    if (hasPendingReport) {
                        emitEffect { HomeEffect.OnResumingAfterPurchase(statementCount) }
                    } else {
                        emitEffect { HomeEffect.OnPurchaseProcessing }
                    }

                    val granted = pollEntitlementUntilCanCreate()
                    if (!granted) {
                        setState {
                            copy(
                                isConfirmingPurchase = false,
                                uploadProgressLabel = if (hasPendingReport) {
                                    "Payment is still processing. Your statements are saved — hang tight, then tap Upload again."
                                } else {
                                    null
                                },
                            )
                        }
                        emitEffect {
                            HomeEffect.OnError(
                                "Payment is still processing. Wait a moment, then try again.",
                            )
                        }
                        return@launch
                    }

                    val paths = PendingReportStore.load() ?: latestState.pendingStoragePaths
                    if (!paths.isNullOrEmpty()) {
                        PendingReportStore.clear()
                        analytics.logClickEvent(EventName.PURCHASE_COMPLETED)
                        createReportFromStoragePaths(paths)
                    } else {
                        setState {
                            copy(
                                isConfirmingPurchase = false,
                                uploadProgressLabel = null,
                            )
                        }
                        analytics.logClickEvent(EventName.PURCHASE_COMPLETED)
                        emitEffect {
                            HomeEffect.OnPurchaseComplete(
                                "Payment received! Upload your statements to create your report.",
                            )
                        }
                    }
                }
                PurchaseReturn.Cancelled -> {
                    PendingReportStore.clear()
                    analytics.logClickEvent(EventName.PURCHASE_CANCELLED)
                    setState {
                        copy(
                            isPurchasing = false,
                            isConfirmingPurchase = false,
                            paywall = null,
                            uploadProgressLabel = null,
                            pendingStoragePaths = null,
                        )
                    }
                    emitEffect { HomeEffect.OnError(purchaseCancelledMessage()) }
                }
            }
        }
    }

    private fun pendingReportResumeLabel(statementCount: Int): String {
        val label = if (statementCount == 1) "1 statement" else "$statementCount statements"
        return "Payment received! Creating your spending story from $label…"
    }

    private suspend fun pollEntitlementUntilCanCreate(): Boolean {
        repeat(20) { attempt ->
            if (attempt > 0) {
                delay(2_000)
            }
            spendTelService.getEntitlement()
                .onSuccess { entitlement ->
                    setState { copy(entitlement = entitlement) }
                    if (entitlement.canCreateReport) return true
                }
        }
        return false
    }

    private fun paywallMessage(entitlement: BillingEntitlement): String {
        val price = normalizePriceLabel(entitlement.priceLabel)
        return when {
            entitlement.freeReportsRemaining > 0 ->
                "Your first spending story is free."
            else ->
                "You've used your free report. Get another for $price."
        }
    }

    private fun deleteReport(reportId: String) {
        launch {
            val userId = latestState.currentUser?.uid ?: authenticationRepo.getCurrentUser()?.uid
            if (userId.isNullOrBlank()) {
                emitEffect { HomeEffect.OnError("You must be signed in to delete a report.") }
                return@launch
            }

            setState { copy(isDeletingReportId = reportId) }

            expenseReportRepo.deleteExpenseReport(userId, reportId)
                .onSuccess {
                    analytics.logClickEvent(
                        EventName.REPORT_DELETED,
                        mapOf("report_id" to reportId),
                    )
                    val updatedReports = latestState.expenseReports.filter { it.id != reportId }
                    setState {
                        copy(
                            isDeletingReportId = null,
                            expenseReports = updatedReports,
                            hasExpenseReports = updatedReports.isNotEmpty(),
                            showEmptyState = updatedReports.isEmpty(),
                        )
                    }
                    emitEffect { HomeEffect.OnReportDeleted }
                    loadHomeState()
                }
                .onFailure { error ->
                    Napier.e("deleteExpenseReport failed: $error")
                    setState { copy(isDeletingReportId = null) }
                    emitEffect {
                        HomeEffect.OnError(
                            error.message ?: "Could not delete this report. Please try again.",
                        )
                    }
                }
        }
    }

    private fun signOut() {
        launch {
            authenticationRepo.signOut()
            emitEffect { HomeEffect.OnSignedOut }
        }
    }
}

data class PaywallUiState(
    val message: String,
    val priceLabel: String,
    val isPurchasing: Boolean = false,
    val errorMessage: String? = null,
)

data class HomeState(
    val isLoading: Boolean = true,
    val currentUser: SpendtelUser? = null,
    val isFirstVisit: Boolean = false,
    val hasExpenseReports: Boolean = false,
    val showEmptyState: Boolean = true,
    val expenseReports: List<ExpenseReport> = emptyList(),
    val isPickingStatements: Boolean = false,
    val isUploadingStatements: Boolean = false,
    val isCreatingReport: Boolean = false,
    val uploadProgressLabel: String? = null,
    val uploadError: String? = null,
    val isDeletingReportId: String? = null,
    val entitlement: BillingEntitlement? = null,
    val paywall: PaywallUiState? = null,
    val pendingStoragePaths: List<StoragePathInput>? = null,
    val isPurchasing: Boolean = false,
    val isConfirmingPurchase: Boolean = false,
) : ViewState

sealed class HomeEvent : ViewEvent {
    data object OnSelectStatements : HomeEvent()
    data object OnSignOut : HomeEvent()
    data object OnRefresh : HomeEvent()
    data object OnRefreshEntitlement : HomeEvent()
    data class OnDeleteReport(val reportId: String) : HomeEvent()
    data object OnDismissPaywall : HomeEvent()
    data object OnPurchaseReport : HomeEvent()
    data object OnBuyReportCredit : HomeEvent()
    data class OnPurchaseReturn(val returnType: PurchaseReturn) : HomeEvent()
}

sealed class HomeEffect : ViewEffect {
    data class OnExpenseReportCreated(val response: ExpenseReportResponse) : HomeEffect()
    data class NavigateToExpenseReport(val reportId: String) : HomeEffect()
    data object OnReportDeleted : HomeEffect()
    data class OnError(val message: String) : HomeEffect()
    data object OnSignedOut : HomeEffect()
    data class OnPurchaseComplete(val message: String) : HomeEffect()
    data object OnPurchaseProcessing : HomeEffect()
    data class OnResumingAfterPurchase(val statementCount: Int) : HomeEffect()
}
